import * as React from 'react'
import * as ReactDOM from 'react-dom'
import { StyleSheetManager, ThemeProvider } from 'styled-components'
import styled from 'styled-components'
import RollbarComponent from '../../components/shared/rollbar.component'
import { personalizedTheme } from '../../theme'
import { I18nService } from './i18n.service'
import TooltipComponent from '../../components/shared/tooltip.component'
import i18nScopedData from './modals.service.yml'

const i18nScoped = new I18nService(i18nScopedData)

export const ModalHeaderIcon = styled.a`
  font-size: 1.5rem;
  transition: all 0.3s ease-in-out;
  color: ${ props => props.theme.colors.clr_0 } !important;
  &:hover { opacity: 0.7; }
  margin-left: 0.5em;
`

export const ModalTable = styled.table`
  td {
    padding: 0.5em 1em !important;
  }

  th {
    background-color: #E9EEF1;
    font-weight: bold;
    text-align: left;
  }
`

export function ModalHeaderIconWithTooltip ({ tooltipMessage, ...iconProps }: React.ComponentProps<typeof ModalHeaderIcon> & { tooltipMessage: string }) {
  return <TooltipComponent message={ tooltipMessage } noIcon style={{ display: 'inline-block' }} innerStyle={{ left: 'unset', right: 0, whiteSpace: 'nowrap', width: 'fit-content' }}>
    <ModalHeaderIcon { ...iconProps } />
  </TooltipComponent>
}

export interface ModalControls<T> {
  onResult: (result: T) => void
  onCancel: () => void
  onDismiss?: () => void
}

interface ModalDecorationOptions {
  blockerClass?: string
  blockerStyle?: React.CSSProperties
  outerModalStyle?: React.CSSProperties
  outerModalClass?: string
  unclosable?: boolean
  helpUrl?: string
  iconElements?: React.ReactNode[]
}

type ModalContentGenerator<T> = (controls: ModalControls<T>) => JSX.Element

export function modalOpenConfirm (title: React.ReactNode, message?: React.ReactNode) {
  return modalOpenDecorated<boolean>(title, controls => <div className='modal-inner' >
    { message && <div style={{ whiteSpace: 'pre-wrap' }} className="u-mbm u-pbm u-borderB">{ message }</div> }

    <div style={{ display: 'flex', justifyContent: 'space-around' }}>
      <button
        id="js-test-modal-cancel-button"
        className='buttonNeutral'
        onClick={ () => controls.onResult(false) }
        children={ i18nScoped.t('cancel') } />

      <button
        id="js-test-modal-confirm-button"
        className='button'
        autoFocus
        onClick={ () => controls.onResult(true) }
        children={ i18nScoped.t('confirm') } />
    </div>
  </div>)
}

export function modalOpenAlert (title: React.ReactNode, message?: React.ReactNode) {
  return modalOpenDecorated<boolean>(title, controls => <div className='modal-inner' >
    { message && <div style={{ whiteSpace: 'pre-wrap' }} className="u-mbm u-pbm u-borderB">{ message }</div> }
  
    <div style={{ display: 'flex', justifyContent: 'space-around' }}>
      <button
          id="js-test-modal-confirm-button"
          className='button'
          autoFocus
          onClick={ () => controls.onResult(true) }
          children={ i18nScoped.t('i_have_understand') } />
    </div>
  </div>)
}

export function modalOpenPrompt (title: React.ReactNode, message?: React.ReactNode, value?: string) {
  return modalOpenDecorated<string>(title, controls => <div className="modal-inner">
    <form onSubmit={ evt => {
      evt.preventDefault()
      controls.onResult((evt.currentTarget.elements[0] as HTMLInputElement).value)
    } }>
      <div style={{ whiteSpace: 'pre-wrap' }} className="u-mbm u-pbm u-borderB">
        { message && <label htmlFor="promptInput">{ message }</label> }
        <input type="text" id="promptInput" defaultValue={ value ?? '' } />
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-around' }}>
        <button
          id="js-test-modal-cancel-button"
          className="buttonNeutral"
          onClick={ () => controls.onCancel() }
          children={ i18nScoped.t('cancel') }
        />

        <button
          id="js-test-modal-confirm-button"
          className="button"
          type="submit"
          children={ i18nScoped.t('submit') } 
        />
      </div>
    </form>
  </div>)
}

/**
 * Open a new modal from a component with the following props :
 * - onResolve(result: T) => void
 * - onCancel() => void
 */
export function modalOpenDecorated<T> (title: React.ReactNode, contentGenerator: ModalContentGenerator<T>, options: ModalDecorationOptions & { portalAnchor?: HTMLElement } = {}) {
  return modalOpen<T>(
    controls => <ModalDecoration title={ title } contentGenerator={ contentGenerator } options={ options } controls={ controls } />,
    { portalAnchor: options.portalAnchor }
  )
}

export function modalOpen<T> (contentGenerator: ModalContentGenerator<T>, options: { portalAnchor?: HTMLElement } = {}) {
  const modalContainer = document.createElement('div');
  (options.portalAnchor || document.body).appendChild(modalContainer)

  return new Promise<T>(resolve => {
    const onResult = (result: T) => { setTimeout(modalCleanup.bind(null, modalContainer), 1); resolve(result) }
    const onCancel = () => { setTimeout(modalCleanup.bind(null, modalContainer), 1); resolve(null) }
    const onDismiss = () => setTimeout(modalCleanup.bind(null, modalContainer), 1)
    const content = contentGenerator({ onResult, onCancel, onDismiss })

    const modal = <RollbarComponent>
      <ThemeProvider theme={ personalizedTheme(window.marketColors) }>
        <StyleSheetManager>
          { content }
        </StyleSheetManager>
      </ThemeProvider>
    </RollbarComponent>

    ReactDOM.render(modal, modalContainer)
  })
}

interface ModalDecorationProps<T>  { 
  controls: ModalControls<T>
  options?: ModalDecorationOptions
  title: React.ReactNode
  children?: React.ReactNode
  contentGenerator?: ModalContentGenerator<T>
}

export function ModalDecoration<T> ({ controls, options = {}, title, children, contentGenerator }: ModalDecorationProps<T>) {
  return <div className={ `jquery-modal blocker ${options.blockerClass || ''}` } style={ options.blockerStyle }>
    <div className={ `modal u-borderRadius0375 modal-container ${ options.outerModalClass || '' }` } style={ options.outerModalStyle }>
      <div className="modal-header" style={ options.helpUrl ? { paddingRight: '5.625rem' } : {} }>
        { title && <h3 className="modal-header-title">{ title }</h3> }

        <div style={{ position: 'absolute', padding: '0.8em', top: 0, right: 0 }}>
          { options.helpUrl && <ModalHeaderIcon className='icon-question' href={ options.helpUrl } target="_blank" rel="noopener noreferrer" /> }
          { options.iconElements }
          { !options.unclosable && <ModalHeaderIcon className="icon-remove js-test-close-modal" onClick={ controls.onCancel } /> }
        </div>
      </div>

      { children || contentGenerator(controls) }
    </div>
  </div>
}

function modalCleanup (modalContainer: HTMLDivElement) {
  ReactDOM.unmountComponentAtNode(modalContainer)
  modalContainer.remove()
}
